import { Dialog, Transition } from "@headlessui/react";
import { ClipboardDocumentIcon, EyeIcon } from "@heroicons/react/24/solid";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { toast, Toaster } from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import Breadcrumbs from "../../components/Breadcrumbs";
import ColoredBadge from "../../components/ColoredBadge";
import Header from "../../components/Header";
import LoadingOverlay from "../../components/LoadingOverlay";
import NewBadge from "../../components/NewBadge";
import Sidebar from "../../components/Sidebar";
import AddSubscriptionModal from "../../components/client/AddSubscriptionModal";
import AnnualClientActivityReview from "../../components/client/AnnualClientActivityReview";
import CloneSubscriptionModal from "../../components/client/CloneSubscriptionModal";
import AssignEmployeToTaskWhenServiceStarts from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/AssignEmployeeToTaskWhenServiceStarts";
import FirmFlowTable from "../../components/mytasks/FirmFlowTable";
import DueDateSummarySection, {
    DUE_DATE_REPORT_ITEMS,
} from "../../components/reports/DueDateSummarySection";
import { usePermissions } from "../../context/PermissionsContext";
import { getEfileStatusDisplayLabel } from "../../helpers/eFileStatus";
import { getClientSubscriptionTypeLabel } from "../../helpers/clientSubscriptionTypeLabel";
import { ensureOneTeamPerDepartment } from "../../helpers/clientTeamRule";
import {
    getClientServiceSubscriptionId,
    getClientServiceName,
    getInvalidTeamAssignmentServiceIds,
    getIsTeamAssignmentValid,
    getTeamAssignmentIssue,
    getTeamAssignmentIssueMessage,
} from "../../helpers/serviceTeamAssignment";
import { getDueDateStatusFromDate } from "../../helpers/dueDateStatus";
import { formatDate, formatLocalIsoDate } from "../../helpers/formatDate";
import { formatTimestampDate } from "../../helpers/formatDateTime";
import { formatPhoneNumber } from "../../helpers/formatPhoneNumber";
import { formatTaxNumber } from "../../helpers/formatTaxNumber";
import { sortClientServices } from "../../helpers/sortClientServices";
import { deleteSubscription } from "../../services/deleteSubscription";
import { getClientService } from "../../services/clientService/getClientService";
import { getClient } from "../../services/getClient";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import { getServices } from "../../services/getServices";
import { getSubscriptions } from "../../services/getSubscriptions";
import DeleteModal from "../../utils/DeleteModal";
import UpdateClientModal from "./UpdateClientModal";
import UpdateClientServiceModal from "./UpdateClientServiceModal";
import { normalizeClientServiceInterval } from "../../helpers/intervalTypesMap";
import DepartmentBadge from "../../components/deparments/DepartmentBadge";
import UpdateClientTeamsModal from "../../components/client/UpdateClientTeamsModal";
import TeamRoleBadge from "../../components/teamRoles/TeamRoleBadge";
import {
    getTeamMemberRoleColorHex,
    getTeamMemberRoleName,
} from "../Teams/teamMembersUtils";

import { getClientDueDateSummary } from "../../services/client/getClientDueDateSummary";
import { useClientInactivityConfirmation } from "../../context/ClientInactivityConfirmationContext";
import {
    getConfirmedInactiveClientState,
    mergeConfirmedInactiveClientState,
} from "../../helpers/clientActivityTransition";
import { getClientServiceMessage } from "../../helpers/clientServiceMessage";

const NO_CHANGES_REVIEW_MESSAGE =
    "This client remains due for review. You can continue viewing the client now, but you'll be asked again the next time you open this page unless new activity is recorded.";

export const isNoChangeAnnualReviewResult = (responseData) => {
    const outcomes = [
        responseData,
        responseData?.data,
        responseData?.result,
        responseData?.data?.result,
    ];
    const requestSucceeded = outcomes.some(
        (outcome) => outcome?.success === true,
    );

    return (
        requestSucceeded &&
        outcomes.some(
            (outcome) =>
                outcome?.reviewCompleted === false &&
                outcome?.changesApplied === false,
        )
    );
};

const getCreatedSubscriptionId = (value) => {
    if (!value || typeof value !== "object") return null;

    const directKeys = [
        "clientServiceSubscriptionId",
        "ClientServiceSubscriptionId",
        "subscriptionId",
        "clientSubscriptionId",
        "id",
    ];

    for (let key of directKeys) {
        if (value[key]) return value[key];
    }

    const nestedKeys = [
        "clientServiceSubscription",
        "subscription",
        "clientSubscription",
        "data",
    ];

    for (let key of nestedKeys) {
        const nestedId = getCreatedSubscriptionId(value[key]);
        if (nestedId) return nestedId;
    }

    return null;
};

const StartCreatedServiceConfirmationModal = ({
    open,
    onNo,
    onYes,
}) => (
    <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[110]" onClose={onNo}>
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-[120] overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                            <div className="text-center">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900"
                                >
                                    Would you like to start the service?
                                </Dialog.Title>
                            </div>

                            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3">
                                <button
                                    type="button"
                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-teal-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:text-sm"
                                    onClick={onYes}
                                >
                                    Yes
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:mt-0 sm:text-sm"
                                    onClick={onNo}
                                >
                                    No
                                </button>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Dialog>
    </Transition.Root>
);

function ManageClients() {
    const { can } = usePermissions();
    const { requestInactiveConfirmation } = useClientInactivityConfirmation();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [list, setList] = useState([]);
    const [services, setServices] = useState([
        { id: 1, name: "Add some services first" },
    ]);
    const [open, setOpen] = useState(false);
    const [openCloneSubscriptionModal, setOpenCloneSubscriptionModal] =
        useState(false);
    const [clientServiceId, setClientServiceId] = useState(0);
    const [clientService, setClientService] = useState(null);
    const [openEditClientModal, setOpenEditClientModal] = useState(false);
    const [openEditTeamsModal, setOpenEditTeamsModal] = useState(false);
    const [loggedInEmployee, setLoggedInEmployee] = useState({});
    const [isStartServiceModalOpen, setIsStartServiceModalOpen] = useState(false);
    const [isStartCreatedServiceConfirmOpen, setIsStartCreatedServiceConfirmOpen] =
        useState(false);
    const [createdSubscriptionToStart, setCreatedSubscriptionToStart] =
        useState(null);
    const [showStartRequirementNote, setShowStartRequirementNote] =
        useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [subscriptionIdToDelete, setSubscriptionIdToDelete] = useState(null);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [overlayMessage, setOverlayMessage] = useState("Loading...");
    const [activeDueDateRange, setActiveDueDateRange] = useState(null); // "pastDue" | "next7days" | "dueToday" | "thereafter" | null
    const [missingTeamServiceIds, setMissingTeamServiceIds] = useState([]);
    const [currentMissingTeamService, setCurrentMissingTeamService] =
        useState(null);
    const [isLoadingMissingTeamService, setIsLoadingMissingTeamService] =
        useState(false);
    const [isTeamRepairModalOpen, setIsTeamRepairModalOpen] = useState(false);
    const [teamRepairProgress, setTeamRepairProgress] = useState({
        current: 0,
        total: 0,
    });
    const [reviewDismissedForCurrentVisit, setReviewDismissedForCurrentVisit] =
        useState(false);
    const activeClientIdRef = useRef(params.client_id);
    const teamRepairGenerationRef = useRef(0);
    const repairSubscriptionsRef = useRef([]);
    const dueDateSummaryRequestRef = useRef({
        clientId: null,
        promise: null,
    });
    const consumedCompletedServiceIdRef = useRef(null);
    const clientRequestVersionRef = useRef(0);
    const confirmedInactiveClientStateRef = useRef(null);

    const routeConfirmedInactiveClientState =
        location.state?.confirmedInactiveClientState;
    if (
        routeConfirmedInactiveClientState?.clientId &&
        String(routeConfirmedInactiveClientState.clientId) ===
            String(params.client_id)
    ) {
        confirmedInactiveClientStateRef.current =
            routeConfirmedInactiveClientState;
    }

    const GUID_EMPTY = "00000000-0000-0000-0000-000000000000";
    const noPermissionMessage = "You don't have permission to do this.";
    const dueDateReportItems = [DUE_DATE_REPORT_ITEMS[0]];
    const canReadClientServices = can("client_services.read");
    const canWriteClientServices = can("client_services.write");
    const canDeleteClientServices = can("client_services.delete");
    const annualReviewOpen =
        client?.requiresAnnualReview === true &&
        !reviewDismissedForCurrentVisit;
    activeClientIdRef.current = params.client_id;

    const updateTeamRepairQueue = (subscriptions, clientId) => {
        if (String(activeClientIdRef.current) !== String(clientId)) return [];

        repairSubscriptionsRef.current = subscriptions;
        const ids = getInvalidTeamAssignmentServiceIds(subscriptions);

        setMissingTeamServiceIds(ids);
        setTeamRepairProgress((previous) => {
            if (ids.length === 0) return { current: 0, total: 0 };
            if (previous.total > 0) return previous;
            return { current: 1, total: ids.length };
        });
        return ids;
    };

    useEffect(() => {
        clientRequestVersionRef.current += 1;
        if (
            confirmedInactiveClientStateRef.current &&
            String(confirmedInactiveClientStateRef.current.clientId) !==
                String(params.client_id)
        ) {
            confirmedInactiveClientStateRef.current = null;
        }
        teamRepairGenerationRef.current += 1;
        repairSubscriptionsRef.current = [];
        setClient(null);
        setList([]);
        setMissingTeamServiceIds([]);
        setCurrentMissingTeamService(null);
        setIsLoadingMissingTeamService(false);
        setIsTeamRepairModalOpen(false);
        setTeamRepairProgress({ current: 0, total: 0 });
        setOpenEditTeamsModal(false);
        setReviewDismissedForCurrentVisit(false);
    }, [params.client_id]);

    useEffect(() => {
        const completedServiceId = location.state?.completedServiceId;
        const hasConfirmedInactiveState = Boolean(
            location.state?.confirmedInactiveClientState,
        );

        if (
            !completedServiceId ||
            consumedCompletedServiceIdRef.current === completedServiceId
        ) {
            if (hasConfirmedInactiveState) {
                const remainingState = { ...location.state };
                delete remainingState.confirmedInactiveClientState;
                navigate(location.pathname, {
                    replace: true,
                    state: remainingState,
                });
            }
            return;
        }

        consumedCompletedServiceIdRef.current = completedServiceId;
        const remainingState = { ...location.state };
        delete remainingState.completedServiceId;
        delete remainingState.confirmedInactiveClientState;

        toast.success("Status changed");
        navigate(location.pathname, {
            replace: true,
            state: remainingState,
        });
    }, [location.pathname, location.state, navigate]);

    useEffect(() => {
        if (
            !location.state?.openClientTeams ||
            !client ||
            annualReviewOpen
        ) {
            return;
        }

        const remainingState = { ...location.state };
        delete remainingState.openClientTeams;
        setOpenEditTeamsModal(true);
        navigate(location.pathname, {
            replace: true,
            state: remainingState,
        });
    }, [annualReviewOpen, client, location.pathname, location.state, navigate]);

    const navigateToService = (serviceId) => {
        if (!canReadClientServices) {
            toast.error(noPermissionMessage);
            return;
        }
        navigate(`/clients/service/${serviceId}/client/${client.id}`);
    };

    const getMidnight = (d) => {
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return x;
    };

    const isCompletedSubscription = (row) => {
        const s = row?.status;
        return s === 2 || s === "Complete" || s === "COMPLETE";
    };
    const getServiceTemplateMeta = (row) => {
        const isCurrent =
            row?.serviceTemplateIsCurrent ??
            row?.ServiceTemplateIsCurrent ??
            row?.serviceTemplate?.isCurrent ??
            row?.serviceTemplate?.IsCurrent ??
            true;
        const versionNumber =
            row?.serviceTemplateVersionNumber ??
            row?.ServiceTemplateVersionNumber ??
            row?.serviceTemplate?.versionNumber ??
            row?.serviceTemplate?.VersionNumber ??
            1;
        return {
            isCurrent,
            versionNumber,
        };
    };

    const matchesRange = (row, range) => {
        if (!range) return true;

        // Exclude completed subscriptions to match backend summary behavior
        if (isCompletedSubscription(row)) return false;

        const due = getMidnight(row?.subscriptionEndDate);
        if (Number.isNaN(due.getTime())) return false;

        const today = getMidnight(new Date());
        const next7End = new Date(today);
        next7End.setDate(next7End.getDate() + 7);

        if (range === "pastDue") return due < today;
        if (range === "dueToday") return due.getTime() === today.getTime();
        if (range === "next7days") return due >= today && due <= next7End;
        if (range === "thereafter") return due > next7End;

        return true;
    };

    useEffect(() => {
        const init = async () => {
            setOverlayMessage("Loading client...");
            setIsOverlayOpen(true);
            try {
                const loadedClient = await callApi(params.client_id);
                if (canReadClientServices && loadedClient?.id) {
                    await fetchClientSubscriptions(false, loadedClient?.id);
                } else {
                    setList([]);
                }
            } finally {
                setIsOverlayOpen(false);
            }
        };
        init();
        getNewLoggedInEmployee();
        // eslint-disable-next-line
    }, [canReadClientServices, params.client_id]);

    useEffect(() => {
        validateClient(client);
        // eslint-disable-next-line
    }, [client]);

    const validateClient = (c) => {
        if (c === null) return false;

        if (
            c.requiresAnnualReview === true &&
            !reviewDismissedForCurrentVisit
        ) {
            setOpenEditTeamsModal(false);
            return false;
        }

        const result = ensureOneTeamPerDepartment(c.teams ?? []);
        if (!result.ok) {
            setOpenEditTeamsModal(true);
            toast.error(
                result.message ||
                    "There are currently multiple teams assigned to the same Department. Only one team can be assigned per department.",
            );
            return false;
        }

        return true;
    };

    const handleManageTeamsFromAddService = () => {
        setOpen(false);
        setOpenEditTeamsModal(true);
    };

    const handleManageTeamsFromRepair = () => {
        setIsTeamRepairModalOpen(false);
        setCurrentMissingTeamService(null);
        setOpenEditTeamsModal(true);
    };

    const capitalizeFirstLetter = (text) => {
        if (!text) return "";
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    };

    const getEmployeeDisplayName = (value) => {
        if (!value) return "-";

        if (typeof value === "string") {
            return value.trim() || "-";
        }

        if (typeof value === "object") {
            if (typeof value.fullName === "string" && value.fullName.trim() !== "") {
                return value.fullName;
            }

            const firstName = value.firstName ?? "";
            const lastName = value.lastName ?? "";
            const fullName = `${firstName} ${lastName}`.trim();

            return fullName || "-";
        }

        return "-";
    };

    const getNewLoggedInEmployee = async () => {
        const currentEmployee = await getLoggedInEmployee();
        if (currentEmployee.success) {
            setLoggedInEmployee(currentEmployee.data.employeeResponse);
        }
    };

    const callApi = async (clientId = params.client_id) => {
        const requestVersion = ++clientRequestVersionRef.current;
        const [clientRes, servicesRes] = await Promise.all([
            getClient(clientId),
            getServices(),
        ]);

        if (
            String(activeClientIdRef.current) !== String(clientId) ||
            requestVersion !== clientRequestVersionRef.current
        ) {
            return null;
        }

        const nextClient = clientRes.success
            ? mergeConfirmedInactiveClientState(
                  clientRes.data,
                  confirmedInactiveClientStateRef.current,
              )
            : null;
        if (nextClient) {
            setClient(nextClient);
            const confirmedState = confirmedInactiveClientStateRef.current;
            if (
                confirmedState &&
                clientRes.data?.isActive === confirmedState.isActive &&
                clientRes.data?.inactiveReason === confirmedState.inactiveReason
            ) {
                confirmedInactiveClientStateRef.current = null;
            }
        }

        if (servicesRes.success) {
            const arr = [...servicesRes.data];
            arr.forEach((service) => (service.is_showing = true));
            setServices(arr);
        }

        return nextClient;
    };

    const refreshClient = async () => {
        const requestVersion = ++clientRequestVersionRef.current;
        const clientRes = await getClient(params.client_id);

        if (
            clientRes.success &&
            String(activeClientIdRef.current) === String(params.client_id) &&
            requestVersion === clientRequestVersionRef.current
        ) {
            const confirmedState = confirmedInactiveClientStateRef.current;
            const nextClient = mergeConfirmedInactiveClientState(
                clientRes.data,
                confirmedState,
            );
            setClient(nextClient);

            if (
                confirmedState &&
                clientRes.data?.isActive === confirmedState.isActive &&
                clientRes.data?.inactiveReason === confirmedState.inactiveReason
            ) {
                confirmedInactiveClientStateRef.current = null;
            }

            return nextClient;
        }

        return null;
    };

    const applyInactiveConfirmationResult = (confirmationResult) => {
        const confirmedState =
            getConfirmedInactiveClientState(confirmationResult);
        if (!confirmedState) return null;

        confirmedInactiveClientStateRef.current = confirmedState;
        clientRequestVersionRef.current += 1;
        setClient((currentClient) =>
            mergeConfirmedInactiveClientState(currentClient, confirmedState),
        );
        return confirmedState;
    };

    async function fetchClientSubscriptions(useOverlay = false, clientIdOverride) {
        if (!canReadClientServices) return [];

        const clientId = clientIdOverride ?? client?.id;

        if (!clientId) return [];

        dueDateSummaryRequestRef.current = {
            clientId: null,
            promise: null,
        };

        if (useOverlay) {
            setOverlayMessage("Refreshing services...");
            setIsOverlayOpen(true);
        }

        try {
            const arr = [];
            const subscriptions = await getSubscriptions(clientId);
            const subscriptionRows =
                subscriptions?.data?.clientServiceSubscriptions || [];

            if (String(activeClientIdRef.current) !== String(clientId)) return null;

            if (!subscriptions?.success || !Array.isArray(subscriptionRows)) {
                setList([]);
                return null;
            }

            subscriptionRows.forEach((sub) => {
                sub.clientId = clientId;
                sub.interval = normalizeClientServiceInterval(sub.interval);
                sub.intervalType = normalizeClientServiceInterval(
                    sub.intervalType,
                );
                sub.clientServiceSubscriptionIntervalType =
                    normalizeClientServiceInterval(
                        sub.clientServiceSubscriptionIntervalType,
                    );
                sub.serviceTemplateIsCurrent =
                    sub.serviceTemplateIsCurrent ??
                    sub.ServiceTemplateIsCurrent ??
                    sub.serviceTemplate?.isCurrent ??
                    sub.serviceTemplate?.IsCurrent ??
                    true;
                sub.serviceTemplateVersionNumber =
                    sub.serviceTemplateVersionNumber ??
                    sub.ServiceTemplateVersionNumber ??
                    sub.serviceTemplate?.versionNumber ??
                    sub.serviceTemplate?.VersionNumber ??
                    1;

                const status = getDueDateStatusFromDate(sub.subscriptionEndDate);
                sub.dueDateStatus = status;
                sub.dueDateColorClass = status?.bgClass;

                if (sub.status === 2 || sub.status === "Complete") {
                    sub.dueDateColorClass = "bg-slate-500";
                }

                arr.push(sub);
            });

            const sortedSubscriptions = sortClientServices(arr);

            setList(sortedSubscriptions);
            updateTeamRepairQueue(sortedSubscriptions, clientId);
            return sortedSubscriptions;
        } finally {
            if (useOverlay) setIsOverlayOpen(false);
        }
    }

    useEffect(() => {
        if (
            annualReviewOpen ||
            !canWriteClientServices ||
            openEditTeamsModal ||
            isLoadingMissingTeamService
        ) {
            return;
        }

        const currentId = getClientServiceSubscriptionId(currentMissingTeamService);
        if (
            currentId &&
            missingTeamServiceIds.some((id) => String(id) === String(currentId))
        ) {
            return;
        }

        if (currentId) {
            setCurrentMissingTeamService(null);
            setIsTeamRepairModalOpen(false);
        }

        const nextServiceId = missingTeamServiceIds[0];
        if (!nextServiceId || !client?.id) return;

        const generation = teamRepairGenerationRef.current;
        const clientId = client.id;
        let isCurrent = true;

        const loadMissingTeamService = async () => {
            setIsLoadingMissingTeamService(true);
            const response = await getClientService(nextServiceId);

            if (
                !isCurrent ||
                generation !== teamRepairGenerationRef.current ||
                String(activeClientIdRef.current) !== String(clientId)
            ) {
                return;
            }

            if (!response?.success || !response?.data) {
                toast.error(
                    getClientServiceMessage(
                        response?.message,
                        "The service requiring a team assignment could not be loaded.",
                    ),
                );
                setIsLoadingMissingTeamService(false);
                return;
            }

            const subscription = repairSubscriptionsRef.current.find(
                (item) =>
                    String(getClientServiceSubscriptionId(item)) ===
                    String(nextServiceId),
            );
            const detail = response.data;

            setCurrentMissingTeamService({
                ...detail,
                id: detail?.id ?? detail?.Id ?? nextServiceId,
                team:
                    detail?.team ??
                    detail?.Team ??
                    subscription?.team ??
                    subscription?.Team ??
                    null,
                teamId:
                    subscription?.teamId ??
                    subscription?.TeamId ??
                    detail?.teamId ??
                    detail?.TeamId ??
                    null,
                isTeamAssignmentValid: getIsTeamAssignmentValid(subscription),
                teamAssignmentIssue: getTeamAssignmentIssue(subscription),
            });
            setIsTeamRepairModalOpen(true);
            setIsLoadingMissingTeamService(false);
        };

        loadMissingTeamService();

        return () => {
            isCurrent = false;
            setIsLoadingMissingTeamService(false);
        };
        // eslint-disable-next-line
    }, [
        missingTeamServiceIds,
        currentMissingTeamService,
        canWriteClientServices,
        client?.id,
        annualReviewOpen,
        openEditTeamsModal,
    ]);

    const onTeamRepairSaveSuccess = async () => {
        const currentId = getClientServiceSubscriptionId(
            currentMissingTeamService,
        );
        const refreshedSubscriptions = await fetchClientSubscriptions(
            false,
            client?.id,
        );

        if (!Array.isArray(refreshedSubscriptions)) {
            toast.error(
                "The service was saved, but its team assignment could not be verified.",
            );
            return false;
        }

        const refreshedService = refreshedSubscriptions.find(
            (subscription) =>
                String(getClientServiceSubscriptionId(subscription)) ===
                String(currentId),
        );

        if (getIsTeamAssignmentValid(refreshedService) !== true) {
            const issue = getTeamAssignmentIssueMessage(refreshedService);
            setCurrentMissingTeamService((previous) => ({
                ...previous,
                ...refreshedService,
                team:
                    refreshedService?.team ??
                    refreshedService?.Team ??
                    previous?.team ??
                    null,
            }));
            toast.error(
                issue ||
                    "The service still does not have a valid team assignment. Please correct the team configuration before continuing.",
            );
            return false;
        }

        const remainingIds = getInvalidTeamAssignmentServiceIds(
            refreshedSubscriptions,
        );
        if (remainingIds.length > 0) {
            setTeamRepairProgress((previous) => ({
                ...previous,
                current: Math.min(previous.current + 1, previous.total),
            }));
        }
        setCurrentMissingTeamService(null);
        return true;
    };

    const onTeamRepairDelete = async (serviceId) => {
        if (!canDeleteClientServices) {
            return {
                success: false,
                message: "You don't have permission to delete Client Services.",
            };
        }

        const response = await deleteSubscription(serviceId);
        if (response?.data?.success !== true) {
            return {
                success: false,
                message: getClientServiceMessage(
                    response?.data?.message || response?.message,
                    "The service could not be deleted. Please try again.",
                ),
            };
        }

        setIsTeamRepairModalOpen(false);
        const confirmationResult = await requestInactiveConfirmation(
            response.data?.clientActivityTransition,
        );
        applyInactiveConfirmationResult(confirmationResult);

        const refreshedSubscriptions = await refreshClientAndSubscriptions(false);
        if (!Array.isArray(refreshedSubscriptions)) {
            return {
                success: false,
                message:
                    "The service was deleted, but the remediation queue could not be refreshed.",
            };
        }

        const remainingIds = getInvalidTeamAssignmentServiceIds(
            refreshedSubscriptions,
        );
        setTeamRepairProgress({
            current: remainingIds.length > 0 ? 1 : 0,
            total: remainingIds.length,
        });
        setIsTeamRepairModalOpen(false);
        setCurrentMissingTeamService(null);
        toast.success(
            getClientServiceMessage(
                response.data.message,
                "Service deleted successfully.",
            ),
        );
        return { success: true };
    };

    const getRangeLabel = (range) => {
        if (range === "pastDue") return "Past Due";
        if (range === "dueToday") return "Due today";
        if (range === "next7days") return "Next 7 Days";
        if (range === "thereafter") return "Thereafter";
        return range;
    };

    const loadClientDueDateSummary = useCallback(
        async ({ reportAlias }) => {
            const clientId = client?.id;

            if (!clientId) {
                return null;
            }

            if (
                dueDateSummaryRequestRef.current.clientId !== clientId ||
                !dueDateSummaryRequestRef.current.promise
            ) {
                dueDateSummaryRequestRef.current = {
                    clientId,
                    promise: getClientDueDateSummary({
                        clientId,
                        departmentId: GUID_EMPTY,
                        employeeId: GUID_EMPTY,
                        includeInactive: false,
                    }),
                };
            }

            const res = await dueDateSummaryRequestRef.current.promise;

            if (!res.success) {
                return null;
            }

            const summary = res.data || {};

            if (reportAlias === "pastDue") {
                return {
                    numberOfServices: summary.pastDue ?? 0,
                    services: [],
                };
            }

            if (reportAlias === "next7days") {
                return {
                    numberOfServices: summary.next7Days ?? 0,
                    services: [],
                    dueTodayCount:
                        summary?.dueTodayCount ??
                        summary?.dueToday ??
                        summary?.today ??
                        0,
                    dueTodayServices: [],
                };
            }

            if (reportAlias === "thereafter") {
                return {
                    numberOfServices: summary.thereafter ?? 0,
                    services: [],
                };
            }

            return null;
        },
        [GUID_EMPTY, client?.id],
    );

    const handleDueDateSummarySelection = useCallback(({ reportAlias }) => {
        setActiveDueDateRange(reportAlias);
    }, []);

    const refreshClientAndSubscriptions = async (useOverlay = false) => {
        const refreshedClient = await refreshClient();
        let refreshedSubscriptions = [];

        if (canReadClientServices) {
            refreshedSubscriptions =
                (await fetchClientSubscriptions(useOverlay, refreshedClient?.id)) ||
                [];
        }

        return refreshedSubscriptions;
    };

    const refreshAfterAnnualReview = async (finalizeResponse) => {
        if (isNoChangeAnnualReviewResult(finalizeResponse)) {
            setReviewDismissedForCurrentVisit(true);
            toast(NO_CHANGES_REVIEW_MESSAGE, { icon: "ℹ️" });
        }

        setMissingTeamServiceIds([]);
        setCurrentMissingTeamService(null);
        setIsTeamRepairModalOpen(false);

        const refreshedClient = await refreshClient();
        if (!refreshedClient) {
            throw new Error("The client could not be refreshed.");
        }

        if (canReadClientServices) {
            await fetchClientSubscriptions(false, refreshedClient.id);
        }

        return refreshedClient;
    };

    const newClientService = async () => {
        await refreshClientAndSubscriptions(true);
        setStartServiceModalOpen(false);
        navigateToService(clientServiceId);
    };

    const onHandleRenewService = async (id, svc) => {
        if (!canWriteClientServices) {
            toast.error(noPermissionMessage);
            return;
        }
        if (!canCloneSubscription(svc)) {
            toast.error(
                "This service has no assigned employee. Assign an employee before cloning.",
            );
            return;
        }

        setClientServiceId(id);
        setClientService(svc);
        setOpenCloneSubscriptionModal(true);
    };

    const onClickRemoveSubscription = (subscriptionId) => {
        if (!canDeleteClientServices) {
            toast.error(noPermissionMessage);
            return;
        }
        setIsDeleteModalOpen(true);
        setSubscriptionIdToDelete(subscriptionId);
    };

    const removeSubscription = async () => {
        if (!canDeleteClientServices) {
            toast.error(noPermissionMessage);
            return;
        }
        setOverlayMessage("Deleting service...");
        setIsOverlayOpen(true);
        try {
            const res = await deleteSubscription(subscriptionIdToDelete);
            if (res?.data?.success) {
                setIsDeleteModalOpen(false);
                setIsOverlayOpen(false);
                const confirmationResult = await requestInactiveConfirmation(
                    res.data?.clientActivityTransition,
                );
                applyInactiveConfirmationResult(confirmationResult);
                await refreshClientAndSubscriptions(true);
                toast.success(
                    getClientServiceMessage(
                        res.data.message,
                        "Service deleted successfully.",
                    ),
                );
            } else {
                toast.error(
                    getClientServiceMessage(
                        res?.data?.message || res?.message,
                        "The service could not be deleted. Please try again.",
                    ),
                );
            }
        } finally {
            setIsDeleteModalOpen(false);
            setIsOverlayOpen(false);
        }
    };

    const onHandleClickStartService = (clientId) => {
        if (!canWriteClientServices) {
            toast.error(noPermissionMessage);
            return;
        }
        setClientServiceId(clientId);
        setShowStartRequirementNote(false);
        setIsStartServiceModalOpen(true);
    };

    const onHandleSubscriptionCreated = async ({
        responseData,
        refreshedSubscriptions = [],
    }) => {
        const previousIds = new Set(
            list
                .map((sub) => sub?.clientServiceSubscriptionId)
                .filter(Boolean),
        );
        const newSubscription =
            refreshedSubscriptions.find(
                (sub) =>
                    sub?.clientServiceSubscriptionId &&
                    !previousIds.has(sub.clientServiceSubscriptionId),
            ) || null;
        const createdSubscriptionId =
            newSubscription?.clientServiceSubscriptionId ||
            getCreatedSubscriptionId(responseData);

        if (!createdSubscriptionId) {
            toast.error(
                "The service was created, but it could not be prepared to start.",
            );
            return;
        }

        let createdSubscriptionData = newSubscription || responseData;

        if (!newSubscription) {
            const createdSubscriptionResponse =
                await getClientService(createdSubscriptionId);

            if (createdSubscriptionResponse?.success) {
                createdSubscriptionData =
                    createdSubscriptionResponse.data || responseData;
            }
        }

        setClientServiceId(createdSubscriptionId);
        setCreatedSubscriptionToStart({
            id: createdSubscriptionId,
            data: createdSubscriptionData,
        });
        setIsStartCreatedServiceConfirmOpen(true);
    };

    const closeStartCreatedServiceConfirmation = () => {
        setIsStartCreatedServiceConfirmOpen(false);
        setCreatedSubscriptionToStart(null);
    };

    const confirmStartCreatedService = () => {
        if (!createdSubscriptionToStart?.id) {
            closeStartCreatedServiceConfirmation();
            return;
        }

        setClientServiceId(createdSubscriptionToStart.id);
        setShowStartRequirementNote(true);
        setIsStartCreatedServiceConfirmOpen(false);
        setIsStartServiceModalOpen(true);
    };

    const setStartServiceModalOpen = (nextOpen) => {
        setIsStartServiceModalOpen(nextOpen);

        if (!nextOpen) {
            setShowStartRequirementNote(false);
            setCreatedSubscriptionToStart(null);
        }
    };

    const onHandleClickEditClientModal = () => {
        setOpenEditClientModal(true);
    };

    if (!client) {
        return (
            <>
                <Toaster />
                <LoadingOverlay isLoading={isOverlayOpen} message={overlayMessage} />
            </>
        );
    }

    const filteredList = sortClientServices(
        activeDueDateRange
            ? list.filter((r) => matchesRange(r, activeDueDateRange))
            : list,
    );

    // ✅ Contact Info should render if Business contacts OR Individual spouse OR phone exists
    const showContactInfo =
        (client?.clientDetail?.clientType === "BUSINESS" &&
            hasAnyBusinessContacts(client?.clientDetail)) ||
        (client?.clientDetail?.clientType === "INDIVIDUAL" &&
            hasAnySpouse(client?.clientDetail)) ||
        !!client?.phoneNumber;

    const canCloneSubscription = (sub) => {
        if (!sub) return false;

        const nameRaw =
            sub.assignedEmployee ?? sub.assignedEmployeeName ?? sub.assignedTo ?? "";

        const name = String(nameRaw || "")
            .trim()
            .toLowerCase();

        const id =
            sub.assignedEmployeeId ??
            sub.assignedToEmployeeId ??
            sub.assignedEmployeeGuid ??
            sub.assignedEmployee?.id ??
            null;

        if (typeof id === "string" && id && id !== GUID_EMPTY) return true;
        if (name && name !== "not assigned" && name !== "n/a") return true;

        return false;
    };

    const getNormalizedSubscriptionStatus = (status) =>
        typeof status === "string" ? status.trim().toLowerCase() : status;

    const showActionsColumn = true;

    const subscriptionColumns = [
        {
            Header: "Year",
            accessor: "serviceYear",
            Cell: (props) => (
                <a
                    className="inline-flex rounded-full whitespace-nowrap text-gray hover:text-blue-500"
                    href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${client.id}`}
                >
                    {props.value}
                </a>
            ),
        },
        {
            Header: "Service",
            accessor: "serviceName",
            Cell: (props) => (
                <div className="min-w-[220px] max-w-[360px]">
                    <ReactTooltip
                        type="light"
                        effect="solid"
                        place="top"
                        border={true}
                        borderColor={"rgb(220,220,220)"}
                        className="rounded-md bg-gray-50"
                        multiline={false}
                    />

                    <button
                        data-tip="Go to service"
                        onClick={() =>
                            navigateToService(
                                props.row.original.clientServiceSubscriptionId,
                            )
                        }
                        className="inline-flex max-w-full text-left rounded-full text-gray hover:text-blue-500"
                    >
                        {(() => {
                            const row = props.row.original;
                            const { isCurrent } = getServiceTemplateMeta(row);
                            const showLegacyBadge =
                                isCompletedSubscription(row) && !isCurrent;

                            return (
                                <span className="inline-flex items-center max-w-full gap-2">
                                    <span className="truncate">
                                        {row.isNew === true ? (
                                            <span>
                                                <NewBadge label="New" />{" "}
                                                {row.serviceName}
                                            </span>
                                        ) : (
                                            row.serviceName
                                        )}
                                    </span>
                                    {showLegacyBadge ? (
                                        <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                                            Legacy
                                        </span>
                                    ) : null}
                                </span>
                            );
                        })()}
                    </button>
                </div>
            ),
        },
        {
            Header: "Assigned by",
            accessor: "assignedBy",
            Cell: (props) => (
                <a
                    className="inline-flex rounded-full whitespace-nowrap text-gray hover:text-blue-500"
                    href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${client.id}`}
                >
                    {getEmployeeDisplayName(
                        props.row.original.assignedBy ??
                            props.row.original.assignedByEmployee ??
                            props.row.original.assignedByUser ??
                            props.row.original.createdBy,
                    )}
                </a>
            ),
        },
        {
            Header: "Assigned to",
            accessor: "assignedEmployee",
            Cell: (props) => (
                <a
                    className="inline-flex max-w-[180px] truncate rounded-full text-gray hover:text-blue-500"
                    href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${client.id}`}
                    title={props.value}
                >
                    {props.value}
                </a>
            ),
        },
        {
            Header: "Due Date",
            accessor: "subscriptionEndDate",
            Cell: (props) => {
                const {
                    clientServiceSubscriptionId,
                    dueDateColorClass,
                    dueDateSubLabel,
                    completionLabel,
                    status,
                } = props.row.original;

                const href = `/clients/service/${clientServiceSubscriptionId}/client/${client.id}`;
                const normalizedStatus = getNormalizedSubscriptionStatus(status);
                const isComplete =
                    normalizedStatus === "complete" || normalizedStatus === 2;
                const isTodoOrInProgress =
                    normalizedStatus === "to do" ||
                    normalizedStatus === "in progress" ||
                    normalizedStatus === 0 ||
                    normalizedStatus === 1;
                const showDueSubLabel = isTodoOrInProgress && !!dueDateSubLabel;
                const showCompletionSubLabel = isComplete && !!completionLabel;
                const badgeColorClass = isComplete
                    ? "bg-slate-500"
                    : dueDateColorClass;

                return (
                    <a
                        href={href}
                        className="inline-flex min-w-[110px] flex-col items-start whitespace-nowrap"
                    >
                        <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-md font-medium text-white ${
                                badgeColorClass || "bg-slate-400"
                            }`}
                        >
                            {formatDate(props.value, {
                                dateStyle: "short",
                            })}
                        </span>

                        {showDueSubLabel && (
                            <span className="mt-1 text-xs font-medium whitespace-nowrap text-slate-600">
                                {dueDateSubLabel}
                            </span>
                        )}

                        {showCompletionSubLabel && (
                            <span className="mt-1 text-xs font-medium whitespace-nowrap text-slate-600">
                                {completionLabel}
                            </span>
                        )}
                    </a>
                );
            },
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: (props) => {
                const row = props.row.original;
                const normalizedStatus = getNormalizedSubscriptionStatus(row.status);
                const showStartButton =
                    normalizedStatus === "to do" || normalizedStatus === 0;
                const eFileStatusLabel = getEfileStatusDisplayLabel(row);

                return (
                    <div className="flex min-w-[220px] flex-col items-start gap-1 whitespace-nowrap">
                        <div className="flex items-center gap-3 whitespace-nowrap">
                            <a
                                className="inline-flex rounded-full whitespace-nowrap text-gray hover:text-blue-500"
                                href={`/clients/service/${row.clientServiceSubscriptionId}/client/${client.id}`}
                            >
                                <span
                                    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-medium text-white ${
                                        props.value === "To Do"
                                            ? "bg-red-500"
                                            : props.value == "In Progress"
                                              ? "bg-yellow-500"
                                              : "bg-gray-500"
                                    }`}
                                >
                                    {props.value}
                                </span>
                            </a>

                            {showStartButton && (
                                <button
                                    className="inline-flex shrink-0 items-center whitespace-nowrap rounded-sm border border-transparent bg-teal-500 px-2 py-1.5 text-sm text-white shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    title={
                                        canWriteClientServices
                                            ? "Start"
                                            : noPermissionMessage
                                    }
                                    disabled={!canWriteClientServices}
                                    onClick={() =>
                                        onHandleClickStartService(
                                            row.clientServiceSubscriptionId,
                                        )
                                    }
                                >
                                    Start
                                </button>
                            )}
                        </div>

                        <span className="text-xs font-medium text-slate-500">
                            {eFileStatusLabel}
                        </span>
                    </div>
                );
            },
        },
        {
            Header: "Completion Date",
            accessor: "completionDate",
            Cell: (props) => {
                const { status, completionDate, clientServiceSubscriptionId } =
                    props.row.original;

                const href = `/clients/service/${clientServiceSubscriptionId}/client/${client.id}`;
                const normalizedStatus = getNormalizedSubscriptionStatus(status);
                const isComplete =
                    normalizedStatus === "complete" ||
                    normalizedStatus === "completed" ||
                    normalizedStatus === 2;

                if (!isComplete) {
                    return (
                        <a
                            href={href}
                            className="inline-flex rounded-full whitespace-nowrap text-gray hover:text-blue-500"
                        >
                            <span className="rounded-full whitespace-nowrap text-gray hover:text-blue-500">
                                Not completed
                            </span>
                        </a>
                    );
                }

                if (!completionDate) {
                    return (
                        <a
                            href={href}
                            className="inline-flex rounded-full whitespace-nowrap text-gray hover:text-blue-500"
                        >
                            <span className="whitespace-nowrap text-slate-500">
                                Not assigned
                            </span>
                        </a>
                    );
                }

                return (
                    <a
                        href={href}
                        className="inline-flex rounded-full whitespace-nowrap text-gray hover:text-blue-500"
                    >
                        {formatTimestampDate(completionDate)}
                    </a>
                );
            },
        },
        ...(showActionsColumn
            ? [
                  {
                      Header: "Actions",
                      id: "actions",
                      accessor: "clientServiceSubscriptionId",
                      Cell: (props) => (
                          <div className="flex min-w-[140px] items-center justify-start gap-2 whitespace-nowrap">
                              <ReactTooltip
                                  type="light"
                                  effect="solid"
                                  place="top"
                                  border={true}
                                  borderColor={"rgb(220,220,220)"}
                                  className="rounded-md bg-gray-50"
                                  multiline={false}
                              />

                              <button
                                  data-tip="View"
                                  onClick={() => {
                                      navigateToService(props.value);
                                  }}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-400 hover:text-slate-500"
                              >
                                  <span className="sr-only">Menu</span>
                                  <EyeIcon className="w-5 h-5" />
                              </button>

                              <button
                                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${
                                      !canWriteClientServices
                                          ? "cursor-not-allowed opacity-50"
                                          : ""
                                  }`}
                                  data-tip="Clone"
                                  title={
                                      canWriteClientServices
                                          ? "Clone"
                                          : noPermissionMessage
                                  }
                                  disabled={!canWriteClientServices}
                                  onClick={() =>
                                      onHandleRenewService(
                                          props.value,
                                          props.row.original,
                                      )
                                  }
                              >
                                  <ClipboardDocumentIcon className="w-5 h-5" />
                              </button>

                              {loggedInEmployee.role == 1 && (
                                  <button
                                      className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${
                                          !canDeleteClientServices
                                              ? "cursor-not-allowed opacity-50"
                                              : ""
                                      }`}
                                      data-tip="Delete"
                                      title={
                                          canDeleteClientServices
                                              ? "Delete"
                                              : noPermissionMessage
                                      }
                                      disabled={!canDeleteClientServices}
                                      onClick={() =>
                                          onClickRemoveSubscription(props.value)
                                      }
                                  >
                                      <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          fill="currentColor"
                                          className="p-1 pt-0 h-7 w-7 text-slate-400 hover:text-red-500"
                                      >
                                          <path
                                              fillRule="evenodd"
                                              d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z"
                                              clipRule="evenodd"
                                          />
                                      </svg>
                                  </button>
                              )}
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <>
            <LoadingOverlay isLoading={isOverlayOpen} message={overlayMessage} />
            <section>
                <Toaster />

                <div className="flex h-screen overflow-hidden">
                    <AnnualClientActivityReview
                        open={annualReviewOpen}
                        clientId={client.id}
                        clientName={client.clientName}
                        canWriteClientServices={canWriteClientServices}
                        canDeleteClientServices={canDeleteClientServices}
                        onCompleted={refreshAfterAnnualReview}
                    />
                    <DeleteModal
                        isModalOpen={
                            !annualReviewOpen && isDeleteModalOpen
                        }
                        setIsModalOpen={setIsDeleteModalOpen}
                        onRemove={removeSubscription}
                        subscriptionIdToDelete={subscriptionIdToDelete}
                    />
                    <AssignEmployeToTaskWhenServiceStarts
                        open={
                            !annualReviewOpen && isStartServiceModalOpen
                        }
                        setOpen={setStartServiceModalOpen}
                        loggedInEmployee={loggedInEmployee}
                        clientSubscriptionId={clientServiceId}
                        clientSubscription={createdSubscriptionToStart?.data}
                        newClientService={newClientService}
                        clientId={client.id}
                        showStartRequirementNote={showStartRequirementNote}
                    />
                    <StartCreatedServiceConfirmationModal
                        open={
                            !annualReviewOpen && isStartCreatedServiceConfirmOpen
                        }
                        onNo={closeStartCreatedServiceConfirmation}
                        onYes={confirmStartCreatedService}
                    />
                    <CloneSubscriptionModal
                        setOpen={setOpenCloneSubscriptionModal}
                        open={
                            !annualReviewOpen && openCloneSubscriptionModal
                        }
                        clientServiceId={clientServiceId}
                        clientService={clientService}
                        refreshSubs={() => refreshClientAndSubscriptions(true)}
                        clientId={client.id}
                    />
                    <AddSubscriptionModal
                        open={!annualReviewOpen && open}
                        setOpen={setOpen}
                        services={services}
                        refreshSubs={() => refreshClientAndSubscriptions(true)}
                        client={client}
                        onSubscriptionCreated={onHandleSubscriptionCreated}
                        onManageTeams={handleManageTeamsFromAddService}
                    />
                    <UpdateClientModal
                        open={
                            !annualReviewOpen && openEditClientModal
                        }
                        setOpen={setOpenEditClientModal}
                        client={client}
                        setClient={setClient}
                    />
                    <UpdateClientTeamsModal
                        open={
                            !annualReviewOpen && openEditTeamsModal
                        }
                        setOpen={setOpenEditTeamsModal}
                        client={client}
                        onSaved={refreshClient}
                    />
                    {!annualReviewOpen &&
                    canWriteClientServices &&
                    currentMissingTeamService ? (
                        <UpdateClientServiceModal
                            open={isTeamRepairModalOpen}
                            setOpen={setIsTeamRepairModalOpen}
                            clientService={currentMissingTeamService}
                            clientId={client.id}
                            loggedInEmployee={loggedInEmployee}
                            teamAssignmentRequired={true}
                            teamAssignmentProgress={teamRepairProgress}
                            onSaveSuccess={onTeamRepairSaveSuccess}
                            canDeleteService={canDeleteClientServices}
                            onDeleteService={onTeamRepairDelete}
                            onManageClientTeams={handleManageTeamsFromRepair}
                        />
                    ) : null}

                    <Sidebar
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                        <Header
                            sidebarOpen={sidebarOpen}
                            setSidebarOpen={setSidebarOpen}
                        />

                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <Breadcrumbs
                                    home="/clients"
                                    pages={[
                                        {
                                            name: client?.clientName ?? "",
                                            href: "",
                                            current: true,
                                        },
                                    ]}
                                />

                                {missingTeamServiceIds.length > 0 ? (
                                    <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                        <p className="font-semibold">
                                            Team assignment required
                                            {teamRepairProgress.total > 1
                                                ? ` — ${teamRepairProgress.current} of ${teamRepairProgress.total}`
                                                : ""}
                                        </p>
                                        <p className="mt-1">
                                            {getClientServiceName(
                                                currentMissingTeamService ||
                                                    repairSubscriptionsRef.current.find(
                                                        (subscription) =>
                                                            String(
                                                                getClientServiceSubscriptionId(
                                                                    subscription,
                                                                ),
                                                            ) ===
                                                            String(
                                                                missingTeamServiceIds[0],
                                                            ),
                                                    ),
                                            )} needs a valid team assignment.
                                        </p>
                                        {!canWriteClientServices ? (
                                            <p className="mt-1 text-xs">
                                                A user with permission to edit services
                                                must complete this information.
                                            </p>
                                        ) : null}
                                    </div>
                                ) : null}

                                {/* Page header */}
                                <div className="mb-8">
                                    {/* Top row: Title + actions */}
                                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="min-w-0">
                                            <h1 className="text-2xl md:text-3xl text-slate-800">
                                                <span className="font-bold">
                                                    {client?.clientName}
                                                </span>
                                            </h1>

                                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                                {typeof client?.isActive ===
                                                    "boolean" &&
                                                    (client?.isActive ? (
                                                        <ColoredBadge
                                                            colorClass="bg-teal-500 text-white"
                                                            text="Active"
                                                        />
                                                    ) : (
                                                        <ColoredBadge
                                                            colorClass="bg-red-500 text-white"
                                                            text="Inactive"
                                                        />
                                                    ))}

                                                {getClientSubscriptionTypeLabel(
                                                    client?.subscriptionType,
                                                ) ? (
                                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-medium text-slate-700">
                                                        {getClientSubscriptionTypeLabel(
                                                            client?.subscriptionType,
                                                        )}
                                                    </span>
                                                ) : null}

                                                {client?.created ? (
                                                    <span className="text-sm text-slate-500">
                                                        Created{" "}
                                                        <span className="font-medium text-slate-700">
                                                            {
                                                                new Date(
                                                                    client.created,
                                                                )
                                                                    .toISOString()
                                                                    .split("T")[0]
                                                            }
                                                        </span>
                                                    </span>
                                                ) : null}

                                                {client?.updated ? (
                                                    <span className="text-sm text-slate-500">
                                                        Last modified at{" "}
                                                        <span className="font-medium text-slate-700">
                                                            {formatLocalIsoDate(
                                                                client.updated,
                                                            )}
                                                        </span>
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                                            <button
                                                onClick={() => setOpen(true)}
                                                disabled={!canWriteClientServices}
                                                title={
                                                    canWriteClientServices
                                                        ? ""
                                                        : noPermissionMessage
                                                }
                                                className={`h-10 text-white bg-teal-500 btn ${
                                                    canWriteClientServices
                                                        ? "hover:bg-teal-600"
                                                        : "opacity-50 cursor-not-allowed"
                                                }`}
                                            >
                                                Add new service
                                            </button>

                                            {loggedInEmployee.role == 1 && (
                                                <button
                                                    onClick={() =>
                                                        onHandleClickEditClientModal()
                                                    }
                                                    className="h-10 text-white bg-teal-500 btn hover:bg-teal-600"
                                                >
                                                    Edit Client
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* ✅ Reordered: Contact Info (left) / Client details (middle) / Teams (right) */}
                                    <div className="grid items-stretch grid-cols-1 gap-4 mt-6 lg:grid-cols-12">
                                        {/* Contact Info (left) */}
                                        {showContactInfo ? (
                                            <div className="h-full lg:col-span-3">
                                                <div className="h-full p-4 bg-white border shadow-sm rounded-xl">
                                                    <h2 className="text-sm font-bold text-slate-800">
                                                        Contact Info
                                                    </h2>

                                                    <div className="grid grid-cols-1 mt-3 gap-x-6 gap-y-2">
                                                        {/* Phone moved here */}
                                                        {client?.phoneNumber ? (
                                                            <InfoRow
                                                                label="Phone"
                                                                value={formatPhoneNumber(
                                                                    client.phoneNumber,
                                                                )}
                                                            />
                                                        ) : null}

                                                        {client?.clientDetail
                                                            ?.clientType ===
                                                        "INDIVIDUAL" ? (
                                                            client?.clientDetail
                                                                ?.clientEmail ? (
                                                                <InfoRow
                                                                    label="Email"
                                                                    value={
                                                                        client
                                                                            .clientDetail
                                                                            .clientEmail
                                                                    }
                                                                />
                                                            ) : null
                                                        ) : null}

                                                        {/* Business contacts */}
                                                        {client?.clientDetail
                                                            ?.clientType ===
                                                        "BUSINESS" ? (
                                                            <>
                                                                {client?.clientDetail
                                                                    ?.pointOfContactName ? (
                                                                    <InfoRow
                                                                        label="Main contact name"
                                                                        value={
                                                                            client
                                                                                .clientDetail
                                                                                .pointOfContactName
                                                                        }
                                                                    />
                                                                ) : null}
                                                                {client?.clientDetail
                                                                    ?.pointOfContactEmail ? (
                                                                    <InfoRow
                                                                        label="Main contact email"
                                                                        value={
                                                                            client
                                                                                .clientDetail
                                                                                .pointOfContactEmail
                                                                        }
                                                                    />
                                                                ) : null}
                                                                {client?.clientDetail
                                                                    ?.secondaryPointOfContactName ? (
                                                                    <InfoRow
                                                                        label="Secondary contact name"
                                                                        value={
                                                                            client
                                                                                .clientDetail
                                                                                .secondaryPointOfContactName
                                                                        }
                                                                    />
                                                                ) : null}
                                                                {client?.clientDetail
                                                                    ?.secondaryPointOfContactEmail ? (
                                                                    <InfoRow
                                                                        label="Secondary contact email"
                                                                        value={
                                                                            client
                                                                                .clientDetail
                                                                                .secondaryPointOfContactEmail
                                                                        }
                                                                    />
                                                                ) : null}
                                                            </>
                                                        ) : null}

                                                        {/* Individual spouse */}
                                                        {client?.clientDetail
                                                            ?.clientType ===
                                                        "INDIVIDUAL" ? (
                                                            <>
                                                                {client?.clientDetail
                                                                    ?.spouseName ? (
                                                                    <InfoRow
                                                                        label="Spouse name"
                                                                        value={
                                                                            client
                                                                                .clientDetail
                                                                                .spouseName
                                                                        }
                                                                    />
                                                                ) : null}
                                                                {client?.clientDetail
                                                                    ?.spouseEmail ? (
                                                                    <InfoRow
                                                                        label="Spouse email"
                                                                        value={
                                                                            client
                                                                                .clientDetail
                                                                                .spouseEmail
                                                                        }
                                                                    />
                                                                ) : null}
                                                                {client?.clientDetail
                                                                    ?.spouseTaxNumber ? (
                                                                    <InfoRow
                                                                        label="Spouse tax#"
                                                                        value={formatTaxNumber(
                                                                            {
                                                                                clientType:
                                                                                    "INDIVIDUAL",
                                                                                taxNumber:
                                                                                    client
                                                                                        .clientDetail
                                                                                        .spouseTaxNumber,
                                                                            },
                                                                        )}
                                                                    />
                                                                ) : null}
                                                            </>
                                                        ) : null}

                                                        {client?.clientDetail
                                                            ?.contactMethod ? (
                                                            <InfoRow
                                                                label="Preferred contact"
                                                                value={
                                                                    client
                                                                        .clientDetail
                                                                        .contactMethod
                                                                }
                                                            />
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}

                                        {/* Client details (middle) */}
                                        <div
                                            className={`h-full ${
                                                showContactInfo
                                                    ? "lg:col-span-6"
                                                    : "lg:col-span-8"
                                            }`}
                                        >
                                            <div className="h-full p-4 bg-white border shadow-sm rounded-xl">
                                                <h2 className="text-sm font-bold text-slate-800">
                                                    Client details
                                                </h2>

                                                <div className="grid grid-cols-1 mt-3 gap-x-6 gap-y-2 sm:grid-cols-2">
                                                    {/* ✅ Phone removed from here */}

                                                    {client?.clientDetail
                                                        ?.clientType ? (
                                                        <InfoRow
                                                            label="Client Type"
                                                            value={capitalizeFirstLetter(
                                                                client.clientDetail
                                                                    .clientType,
                                                            )}
                                                        />
                                                    ) : null}

                                                    {client?.taxNumber ? (
                                                        <InfoRow
                                                            label="Tax#"
                                                            value={formatTaxNumber(
                                                                {
                                                                    clientType:
                                                                        client
                                                                            ?.clientDetail
                                                                            ?.clientType,
                                                                    taxNumber:
                                                                        client.taxNumber,
                                                                },
                                                            )}
                                                        />
                                                    ) : null}

                                                    {client?.clientDetail
                                                        ?.language ? (
                                                        <InfoRow
                                                            label="Language"
                                                            value={
                                                                client.clientDetail
                                                                    .language
                                                            }
                                                        />
                                                    ) : null}

                                                    {hasAnyAddress(
                                                        client?.address,
                                                    ) ? (
                                                        <div className="sm:col-span-2">
                                                            <div className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                                                                Address
                                                            </div>
                                                            <div className="mt-1 text-sm text-slate-800">
                                                                {formatAddress(
                                                                    client.address,
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : null}

                                                    {client?.isActive === false ? (
                                                        <div className="p-3 mt-1 rounded-lg sm:col-span-2 bg-slate-50">
                                                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                                {client?.subscriptionCancellationDate ? (
                                                                    <InfoRow
                                                                        label="Inactive Date"
                                                                        value={client.subscriptionCancellationDate.split("T")[0]}
                                                                    />
                                                                ) : null}
                                                                {client?.inactiveReason ? (
                                                                    <InfoRow
                                                                        label="Inactive Reason"
                                                                        value={
                                                                            client.inactiveReason
                                                                        }
                                                                    />
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Teams (right) */}
                                        <div
                                            className={`h-full ${
                                                showContactInfo
                                                    ? "lg:col-span-3"
                                                    : "lg:col-span-4"
                                            }`}
                                        >
                                            <div className="flex flex-col h-full p-4 bg-white border shadow-sm rounded-xl">
                                                <div className="flex items-center justify-between gap-2">
                                                    <h2 className="text-sm font-bold text-slate-800">
                                                        Teams
                                                    </h2>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium text-slate-500">
                                                            {client?.teams?.length ??
                                                                0}
                                                        </span>
                                                        {loggedInEmployee.role ==
                                                            1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setOpenEditTeamsModal(
                                                                        true,
                                                                    )
                                                                }
                                                                className="h-10 text-white bg-teal-500 btn hover:bg-teal-600"
                                                            >
                                                                Edit teams
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {client?.teams?.length ? (
                                                    <ul className="mt-3 space-y-2 overflow-auto">
                                                        {client.teams.map((team) => {
                                                            const deptName =
                                                                typeof team.department ===
                                                                "string"
                                                                    ? team.department
                                                                    : (team
                                                                          ?.department
                                                                          ?.name ??
                                                                      team?.departmentName ??
                                                                      "Unknown");

                                                            const deptColorHex =
                                                                team?.colorHex ??
                                                                team?.department
                                                                    ?.colorHex ??
                                                                null;

                                                            return (
                                                                <li
                                                                    key={team.id}
                                                                    className="p-2 rounded-lg bg-slate-50"
                                                                >
                                                                    <div className="min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <DepartmentBadge
                                                                                name={
                                                                                    deptName
                                                                                }
                                                                                colorHex={
                                                                                    deptColorHex
                                                                                }
                                                                            />
                                                                            <span className="text-sm font-medium truncate text-slate-800">
                                                                                {
                                                                                    team.name
                                                                                }
                                                                            </span>
                                                                        </div>

                                                                        <TeamMembersList
                                                                            members={
                                                                                team?.members
                                                                            }
                                                                        />
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                ) : (
                                                    <p className="mt-3 text-sm text-slate-500">
                                                        No teams assigned
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Due Date Summary BELOW */}
                                    <div className="mt-6">
                                        <DueDateSummarySection
                                            title="Due Date Summary"
                                            selectedItem={dueDateReportItems[0]}
                                            onSelectionChange={
                                                handleDueDateSummarySelection
                                            }
                                            selectedBucketAlias={
                                                activeDueDateRange
                                            }
                                            onSelectedBucketAliasChange={
                                                setActiveDueDateRange
                                            }
                                            showAssignmentSelector={false}
                                            showEmployeeSelector={false}
                                            showDepartmentSelector={false}
                                            showClearSelection={true}
                                            reportItems={dueDateReportItems}
                                            loadReportData={loadClientDueDateSummary}
                                            sectionClassName="mt-0"
                                        />

                                        <div className="min-h-[28px] mt-2 text-sm text-slate-600">
                                            {activeDueDateRange ? (
                                                <>
                                                    Showing{" "}
                                                    <span className="font-semibold text-slate-800">
                                                        {getRangeLabel(
                                                            activeDueDateRange,
                                                        )}
                                                    </span>{" "}
                                                    ({filteredList.length} of{" "}
                                                    {list.length})
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                {/* ====== Table ====== */}
                                {canReadClientServices ? (
                                    <FirmFlowTable
                                        columns={subscriptionColumns}
                                        data={filteredList}
                                    />
                                ) : (
                                    <div className="px-4 py-8 text-sm bg-white border rounded-sm text-slate-500 border-slate-200">
                                        You don't have permission to view client
                                        services.
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>
                </div>
            </section>
        </>
    );
}

function InfoRow({ label, value }) {
    if (value === null || value === undefined) return null;

    const text = typeof value === "string" ? value.trim() : String(value).trim();
    if (!text) return null;

    return (
        <div>
            <div className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                {label}
            </div>
            <div className="mt-1 text-sm break-words text-slate-800">{text}</div>
        </div>
    );
}

function TeamMembersList({ members }) {
    const list = Array.isArray(members) ? members : [];

    if (!list.length) {
        return (
            <p className="mt-2 ml-1 text-xs text-slate-500">
                No team members assigned
            </p>
        );
    }

    return (
        <div className="mt-2 ml-1 space-y-1">
            {list.map((member) => {
                const fullName = getTeamMemberDisplayName(member);
                const roleName = getTeamMemberRoleName(member);
                const roleColorHex = getTeamMemberRoleColorHex(member);

                return (
                    <div
                        key={member?.employeeId ?? `${fullName}-${roleName}`}
                        className="flex items-center justify-between gap-2 text-xs"
                    >
                        <span
                            className="min-w-0 truncate text-slate-600"
                            title={fullName}
                        >
                            {fullName}
                        </span>
                        {roleName ? (
                            <TeamRoleBadge
                                name={roleName}
                                colorHex={roleColorHex}
                                className="shrink-0"
                            />
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function getTeamMemberDisplayName(member) {
    if (!member) return "-";

    const fullName =
        typeof member.fullName === "string" ? member.fullName.trim() : "";

    return fullName || "-";
}

function hasAnyAddress(address) {
    if (!address) return false;
    return Boolean(
        (address.street && String(address.street).trim()) ||
        (address.city && String(address.city).trim()) ||
        (address.stateProvince && String(address.stateProvince).trim()) ||
        (address.country && String(address.country).trim()) ||
        (address.postalCode && String(address.postalCode).trim()),
    );
}

function hasAnyBusinessContacts(detail) {
    if (!detail) return false;

    return Boolean(
        (detail.pointOfContactName && String(detail.pointOfContactName).trim()) ||
        (detail.pointOfContactEmail && String(detail.pointOfContactEmail).trim()) ||
        (detail.secondaryPointOfContactName &&
            String(detail.secondaryPointOfContactName).trim()) ||
        (detail.secondaryPointOfContactEmail &&
            String(detail.secondaryPointOfContactEmail).trim()),
    );
}

function hasAnySpouse(detail) {
    if (!detail) return false;

    return Boolean(
        (detail.spouseName && String(detail.spouseName).trim()) ||
        (detail.spouseEmail && String(detail.spouseEmail).trim()) ||
        (detail.spouseTaxNumber && String(detail.spouseTaxNumber).trim()),
    );
}

function formatAddress(address) {
    if (!address) return "";

    const parts = [
        address.street,
        address.city,
        address.stateProvince,
        address.country,
        address.postalCode,
    ]
        .map((x) => (x ? String(x).trim() : ""))
        .filter(Boolean);

    return parts.join(", ");
}

export default ManageClients;
